import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Input, Label, Select } from '../components/ui';
import { useCreateServiceItem, type ServiceItemInput } from '../lib/serviceMutations';
import {
    SERVICE_PRIORITY_DEFAULT,
    SERVICE_PRIORITY_LABELS,
    SERVICE_PRIORITY_ORDER,
    SERVICE_STATE_LABELS,
    SERVICE_STATE_ORDER,
    type ServiceItemPriority,
    type ServiceItemState,
} from '../lib/serviceTypes';
import { cn } from '../lib/utils';

/**
 * Add a single service item by hand.
 *
 * The board shipped on 10 Aug with no way to add a row. The only path onto it
 * was the one-time seed from Sara's spreadsheet, so anything phoned in or
 * walked over had nowhere to go and the office was back on the sheet for it.
 * Kristine found that on 25 Aug.
 *
 * Deliberately small. Type of work is the only required field, because the
 * common case is somebody capturing a callback while they are still on the
 * phone. Everything else can be filled in from the board afterwards, where
 * every cell is already editable.
 *
 * The modal is hand-rolled rather than reaching for a shared Dialog so this
 * carries no assumption about what components/ui exports beyond the four it
 * imports, all of which are already used by the service board.
 */
export function AddServiceItemDialog({
    open,
    onClose,
    communityOptions,
    crewOptions,
}: {
    open: boolean;
    onClose: () => void;
    communityOptions: { value: string; label: string }[];
    crewOptions: { value: string; label: string }[];
}) {
    const create = useCreateServiceItem();

    const [communityId, setCommunityId] = useState('');
    const [lot, setLot] = useState('');
    const [typeOfWork, setTypeOfWork] = useState('');
    const [builderContact, setBuilderContact] = useState('');
    const [crewId, setCrewId] = useState('');
    const [scheduleState, setScheduleState] = useState<ServiceItemState>('unscheduled');
    const [scheduledDate, setScheduledDate] = useState('');
    const [priority, setPriority] = useState<ServiceItemPriority>(SERVICE_PRIORITY_DEFAULT);
    const [billable, setBillable] = useState(false);
    const [requiresConcrete, setRequiresConcrete] = useState(false);
    const [extendedTime, setExtendedTime] = useState(false);
    const [isWarranty, setIsWarranty] = useState(false);
    const [notes, setNotes] = useState('');
    // The date the request came in, which is usually the date on the email
    // rather than today. Kristine, 3 Sep. Defaults to today so a phoned-in
    // callback still sorts sensibly, but it is hers to change.
    const [requestedDate, setRequestedDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setCommunityId('');
        setLot('');
        setTypeOfWork('');
        setBuilderContact('');
        setCrewId('');
        setScheduleState('unscheduled');
        setScheduledDate('');
        setPriority(SERVICE_PRIORITY_DEFAULT);
        setBillable(false);
        setRequiresConcrete(false);
        setExtendedTime(false);
        setIsWarranty(false);
        setNotes('');
        setRequestedDate(new Date().toISOString().slice(0, 10));
        setError(null);
    }, [open]);

    if (!open) return null;

    async function handleSave() {
        setError(null);
        if (!typeOfWork.trim()) {
            setError('Type of work is required');
            return;
        }

        // A date implies scheduled, and 'scheduled' without a date is a state
        // the check constraint rejects. reconcileSchedule enforces both, so
        // send the pair and let it decide rather than guessing here.
        const input: ServiceItemInput = {
            community_id: communityId || null,
            lot: lot.trim() || null,
            type_of_work: typeOfWork.trim(),
            builder_contact: builderContact.trim() || null,
            crew_id: crewId || null,
            schedule_state: scheduleState,
            scheduled_date: scheduledDate || null,
            priority,
            billable,
            requires_concrete: requiresConcrete,
            extended_time: extendedTime,
            is_warranty: isWarranty,
            notes: notes.trim() || null,
            status: 'open',
            // What the office reads as the request date. Left blank it would
            // sort to the very bottom of a list ordered oldest first, which is
            // where it would never be seen, so it falls back to today.
            email_date: requestedDate || new Date().toISOString().slice(0, 10),
        };

        try {
            await create.mutateAsync(input);
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not add that item');
        }
    }

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10">
            <button
                type="button"
                aria-label="Close"
                className="fixed inset-0 bg-graphite-950/60"
                onClick={onClose}
            />
            <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-graphite-900 border border-graphite-300 dark:border-graphite-700 rounded shadow-card-md">
                <div className="px-5 py-4 border-b border-graphite-200 dark:border-graphite-800">
                    <h2 className="font-display font-semibold text-lg text-graphite-900 dark:text-graphite-100">
                        Add service item
                    </h2>
                    <p className="mt-0.5 text-xs text-graphite-500">
                        Only the work is required. Everything else can be filled in on the board.
                    </p>
                </div>

                <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <Label htmlFor="add-work">Type of work</Label>
                        <textarea
                            id="add-work"
                            rows={3}
                            value={typeOfWork}
                            onChange={(e) => setTypeOfWork(e.target.value)}
                            placeholder="What needs doing"
                            className="w-full bg-white dark:bg-graphite-900 border border-graphite-300 dark:border-graphite-700 rounded px-2 py-1.5 text-sm text-graphite-900 dark:text-graphite-100 focus:outline-none focus:ring-1 focus:ring-signal-500 resize-y"
                        />
                    </div>

                    <div>
                        <Label htmlFor="add-requested">Requested date</Label>
                        <Input
                            id="add-requested"
                            type="date"
                            value={requestedDate}
                            onChange={(e) => setRequestedDate(e.target.value)}
                        />
                    </div>

                    <div>
                        <Label htmlFor="add-community">Community</Label>
                        <Select
                            id="add-community"
                            value={communityId}
                            onChange={(e) => setCommunityId(e.target.value)}
                        >
                            <option value="">(no community)</option>
                            {communityOptions.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="add-lot">Lot</Label>
                        <Input id="add-lot" value={lot} onChange={(e) => setLot(e.target.value)} />
                    </div>

                    <div>
                        <Label htmlFor="add-contact">Builder contact</Label>
                        <Input
                            id="add-contact"
                            value={builderContact}
                            onChange={(e) => setBuilderContact(e.target.value)}
                        />
                    </div>

                    <div>
                        <Label htmlFor="add-crew">Crew</Label>
                        <Select id="add-crew" value={crewId} onChange={(e) => setCrewId(e.target.value)}>
                            <option value="">No crew</option>
                            {crewOptions.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="add-state">Schedule</Label>
                        <Select
                            id="add-state"
                            value={scheduleState}
                            onChange={(e) => setScheduleState(e.target.value as ServiceItemState)}
                        >
                            {SERVICE_STATE_ORDER.map((s) => (
                                <option key={s} value={s}>
                                    {SERVICE_STATE_LABELS[s]}
                                </option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="add-date">Scheduled date</Label>
                        <Input
                            id="add-date"
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                        />
                    </div>

                    <div>
                        <Label htmlFor="add-priority">Priority</Label>
                        <Select
                            id="add-priority"
                            value={String(priority)}
                            onChange={(e) =>
                                setPriority(Number(e.target.value) as ServiceItemPriority)
                            }
                        >
                            {SERVICE_PRIORITY_ORDER.map((p) => (
                                <option key={p} value={p}>
                                    {SERVICE_PRIORITY_LABELS[p]}
                                </option>
                            ))}
                        </Select>
                    </div>

                    <div className="md:col-span-2 flex flex-wrap gap-4 pt-1">
                        {(
                            [
                                ['Billable', billable, setBillable],
                                ['Concrete', requiresConcrete, setRequiresConcrete],
                                ['Extra time', extendedTime, setExtendedTime],
                                ['Warranty', isWarranty, setIsWarranty],
                            ] as [string, boolean, (v: boolean) => void][]
                        ).map(([label, value, set]) => (
                            <label
                                key={label}
                                className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-graphite-600 dark:text-graphite-400 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={value}
                                    onChange={(e) => set(e.target.checked)}
                                    className="accent-signal-600"
                                />
                                {label}
                            </label>
                        ))}
                    </div>

                    <div className="md:col-span-2">
                        <Label htmlFor="add-notes">Notes</Label>
                        <textarea
                            id="add-notes"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="w-full bg-white dark:bg-graphite-900 border border-graphite-300 dark:border-graphite-700 rounded px-2 py-1.5 text-sm text-graphite-900 dark:text-graphite-100 focus:outline-none focus:ring-1 focus:ring-signal-500 resize-y"
                        />
                    </div>
                </div>

                {error && (
                    <div className={cn(
                        'mx-5 mb-3 px-3 py-2 rounded font-mono text-xs',
                        'border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400',
                    )}>
                        {error}
                    </div>
                )}

                <div className="px-5 py-3 border-t border-graphite-200 dark:border-graphite-800 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        disabled={create.isPending}
                    >
                        {create.isPending ? 'Adding...' : 'Add item'}
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
